import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FileText, Video, Image as ImageIcon, BookOpen, Megaphone, ClipboardList, Download,
  ExternalLink, Loader2, HandHeart, Award, ArrowRight, Sparkles,
} from 'lucide-react'
import { PortalLayout } from '../../components/portal/PortalLayout'
import { Seo } from '../../components/portal/Seo'
import { Container, Section, SectionHeading, Breadcrumbs, Card, Badge, EmptyState, Alert, Button } from '../../components/portal/ui'
import {
  listResources,
  RESOURCE_TYPE_LABELS,
  type Resource,
  type ResourceType,
} from '../../services/resourceService'
import { listTestimonials, type Testimonial } from '../../services/testimonialService'

// NGO resource sections in the order the user specified. Each maps to a
// ResourceType fetched from the library filtered by audience=NGO.
const NGO_SECTIONS: { type: ResourceType; icon: typeof FileText }[] = [
  { type: 'GUIDELINE', icon: ClipboardList },
  { type: 'IEC_MATERIAL', icon: ImageIcon },
  { type: 'TRAINING', icon: Video },
  { type: 'CAMPAIGN_TOOLKIT', icon: Megaphone },
  { type: 'REPORTING_FORMAT', icon: FileText },
]

const TYPE_ICON: Record<string, typeof FileText> = {
  GUIDELINE: FileText, REPORT: FileText, TRAINING: Video, INFOGRAPHIC: ImageIcon,
  VIDEO: Video, PUBLICATION: BookOpen, NOTIFICATION: FileText, MEDIA: ImageIcon,
  IEC_MATERIAL: ImageIcon, CAMPAIGN_TOOLKIT: Megaphone, REPORTING_FORMAT: FileText, OTHER: FileText,
}

export default function NgoHubPage() {
  const { t, i18n } = useTranslation()
  const [items, setItems] = useState<Resource[]>([])
  const [stories, setStories] = useState<Testimonial[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const typeLabel = (k: string) => t(`pages.resources.types.${k}`, { defaultValue: RESOURCE_TYPE_LABELS[k as ResourceType] ?? k })

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const [res, sto] = await Promise.all([
        listResources({ audience: 'NGO', size: 60 }),
        listTestimonials({ role: 'NIKSHAY_MITRA', size: 6 }).catch(() => ({ content: [] as Testimonial[], totalElements: 0, totalPages: 0 } as any)),
      ])
      setItems(res.content)
      setStories((sto.content as Testimonial[]) ?? [])
    } catch (e: any) {
      setError(e?.response?.data?.message || t('pages.ngoHub.error', { defaultValue: 'Could not load materials. Please try again.' }))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => { load() }, [load, i18n.language])

  const target = (r: Resource) => r.externalUrl || r.fileUrl || ''
  const byType = (tp: ResourceType) => items.filter((r) => r.resourceType === tp)

  const renderResourceCard = (r: Resource) => {
    const Icon = TYPE_ICON[r.resourceType] ?? FileText
    const href = target(r)
    const Tag = href ? 'a' : 'div'
    return (
      <Tag
        key={r.id}
        {...(href ? { href, target: '_blank', rel: 'noopener noreferrer' } : {})}
        className={`block ${href ? 'hover:shadow-md transition-shadow cursor-pointer' : ''}`}
      >
        <Card hover={!!href} className="p-5 h-full flex flex-col">
          <div className="flex items-start gap-3 mb-3">
            <span className="w-10 h-10 rounded-lg bg-[rgb(240_252_255)] text-[rgb(15_76_129)] flex items-center justify-center shrink-0" aria-hidden="true">
              <Icon className="w-5 h-5" />
            </span>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-snow-900 line-clamp-2">{r.title}</h3>
              <p className="text-xs text-snow-500 mt-0.5">{typeLabel(r.resourceType)}</p>
            </div>
          </div>
          {r.description && <p className="text-sm text-snow-600 line-clamp-3 flex-1">{r.description}</p>}
          <div className="flex flex-wrap items-center gap-1.5 mt-3">
            {r.fileType && <Badge tone="slate">{r.fileType}</Badge>}
            {!href && <Badge tone="amber">{t('pages.ngoHub.beingCurated', { defaultValue: 'Being curated' })}</Badge>}
          </div>
          {r.sourceOrg && <p className="text-[11px] text-snow-500 mt-2">{r.sourceOrg}</p>}
          {href && (
            <p className="text-xs text-[rgb(15_76_129)] mt-3 inline-flex items-center gap-1">
              {r.externalUrl ? t('pages.resources.openResource', { defaultValue: 'Open resource' }) : t('pages.resources.download', { defaultValue: 'Download' })}
              <ExternalLink className="w-3 h-3" aria-hidden="true" />
            </p>
          )}
        </Card>
      </Tag>
    )
  }

  const renderSection = (tp: ResourceType, icon: typeof FileText, titleKey: string) => {
    const list = byType(tp)
    const Icon = icon
    return (
      <div className="mb-12">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-9 h-9 rounded-lg bg-[rgb(232_241_252)] text-[rgb(15_76_129)] flex items-center justify-center" aria-hidden="true">
            <Icon className="w-5 h-5" />
          </span>
          <h2 className="text-xl font-bold text-snow-900">{t(titleKey)}</h2>
        </div>
        {loading ? (
          <div className="flex items-center text-snow-500 text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" /> {t('common.loading', { defaultValue: 'Loading…' })}</div>
        ) : list.length === 0 ? (
          <EmptyState
            icon={<Download className="w-6 h-6" />}
            title={t('pages.ngoHub.emptyTitle', { defaultValue: 'Materials coming soon' })}
            description={t('pages.ngoHub.emptyDesc', { defaultValue: 'Verified materials for this section are being added. Please check back shortly.' })}
          />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {list.map(renderResourceCard)}
          </div>
        )}
      </div>
    )
  }

  return (
    <PortalLayout>
      <Seo
        title={t('pages.ngoHub.seo.title', { defaultValue: 'NGO Partnership Hub — TB Mukt Bharat' })}
        description={t('pages.ngoHub.seo.description', { defaultValue: 'Resources for NGOs partnering on TB elimination: programme guidelines, IEC materials, training, campaign toolkit and reporting formats.' })}
        canonicalPath="/ngo"
        breadcrumbs={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.ngoHub', { defaultValue: 'NGO Hub' }), path: '/ngo' }]}
      />

      <Section className="pb-6">
        <Container>
          <Breadcrumbs items={[{ name: t('common.home', 'Home'), path: '/' }, { name: t('nav.ngoHub', { defaultValue: 'NGO Hub' }) }]} />
          <SectionHeading
            as="h1"
            eyebrow={t('pages.ngoHub.hero.eyebrow', { defaultValue: 'For organisations' })}
            title={t('pages.ngoHub.hero.title', { defaultValue: 'NGO Partnership Hub' })}
            description={t('pages.ngoHub.hero.description', { defaultValue: 'Everything an NGO needs to partner with the national TB programme and run effective community support — guidelines, materials, training and reporting.' })}
          />
          <div className="flex flex-wrap gap-3 mt-4">
            <Button to="/become-a-supporter?type=NIKSHAY_MITRA_NGO" variant="primary" icon={<HandHeart className="w-4 h-4" />} className="min-h-[44px]">
              {t('pages.ngoHub.registerCta', { defaultValue: 'Register your NGO' })}
            </Button>
            <Button to="/support-a-patient" variant="outline" icon={<ArrowRight className="w-4 h-4" />}>
              {t('pages.ngoHub.learnMore', { defaultValue: 'How NGOs help' })}
            </Button>
          </div>
        </Container>
      </Section>

      <Section className="bg-white pt-8">
        <Container>
          {error && <Alert tone="warning" title={error} className="mb-6" />}

          {/* Curated notice — materials are sample placeholders until admins attach verified docs */}
          <Alert tone="info" title={t('pages.ngoHub.curatedNoticeTitle', { defaultValue: 'Materials are being curated' })} className="mb-8">
            {t('pages.ngoHub.curatedNoticeBody', { defaultValue: 'The resources below are sample placeholders. Verified documents from official sources will be attached by the TB Mukt Bharat team. No government content is fabricated.' })}
          </Alert>

          {/* Registration */}
          <Card className="p-6 mb-12 border-2 border-[rgb(191_237_255)]">
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <span className="w-12 h-12 rounded-xl bg-[rgb(15_76_129)] text-white flex items-center justify-center shrink-0" aria-hidden="true">
                <HandHeart className="w-6 h-6" />
              </span>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-snow-900">{t('pages.ngoHub.registration.title', { defaultValue: 'Register your NGO' })}</h2>
                <p className="text-snow-600 text-sm mt-1">{t('pages.ngoHub.registration.body', { defaultValue: 'Sign up as a Ni-kshay Mitra NGO supporter to partner with the programme. Registration is reviewed by the TB Mukt Bharat team before onboarding.' })}</p>
                <div className="mt-3">
                  <Button to="/become-a-supporter?type=NIKSHAY_MITRA_NGO" variant="primary" icon={<ArrowRight className="w-4 h-4" />} className="min-h-[44px]">
                    {t('pages.ngoHub.registration.button', { defaultValue: 'Start NGO registration' })}
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          {/* Resource sections */}
          {NGO_SECTIONS.map((s) => renderSection(s.type, s.icon, `pages.ngoHub.sections.${s.type}`))}

          {/* Success stories */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-9 h-9 rounded-lg bg-[rgb(232_252_243)] text-[rgb(0_138_88)] flex items-center justify-center" aria-hidden="true">
                <Sparkles className="w-5 h-5" />
              </span>
              <h2 className="text-xl font-bold text-snow-900">{t('pages.ngoHub.sections.successStories', { defaultValue: 'Success stories' })}</h2>
            </div>
            {stories.length === 0 ? (
              <EmptyState
                icon={<Award className="w-6 h-6" />}
                title={t('pages.ngoHub.storiesEmpty', { defaultValue: 'Stories will appear here' })}
                description={t('pages.ngoHub.storiesEmptyDesc', { defaultValue: 'Real, consented stories from NGO supporters will be featured here — never fabricated.' })}
              />
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {stories.map((s) => (
                  <Card key={s.id} className="p-5 flex flex-col">
                    <Badge tone="teal" className="mb-2 self-start">{t('pages.ngoHub.supporterStory', { defaultValue: 'Supporter story' })}</Badge>
                    <h3 className="font-semibold text-snow-900 line-clamp-2">{s.title}</h3>
                    {s.body && <p className="text-sm text-snow-600 line-clamp-4 mt-2 flex-1">{s.body}</p>}
                    {(s.authorName || s.location) && (
                      <p className="text-xs text-snow-500 mt-3">{[s.authorName, s.location].filter(Boolean).join(' · ')}</p>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </div>
        </Container>
      </Section>
    </PortalLayout>
  )
}